import React from 'react';
import { Phone, Mail, ChevronDown } from 'lucide-react';
import { Button } from './ui/button';
import { businessInfo } from '../data/mock';

const Hero = () => {
  const scrollToContact = () => {
    const element = document.getElementById('contact');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <section id="hero" className="relative min-h-screen flex items-center justify-center overflow-hidden">
      {/* Background Image with Overlay */}
      <div className="absolute inset-0 z-0">
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: `url('https://customer-assets.emergentagent.com/job_page-craft-221/artifacts/kmxq1vzn_image.png')`
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-slate-900/70 via-slate-900/60 to-slate-900/80" />
      </div>

      {/* Decorative Elements */}
      <div className="absolute top-20 right-10 w-72 h-72 bg-cyan-500/10 rounded-full blur-3xl" />
      <div className="absolute bottom-20 left-10 w-96 h-96 bg-orange-500/10 rounded-full blur-3xl" />

      {/* Content */}
      <div className="container mx-auto px-4 z-10 pt-8">
        <div className="max-w-6xl mx-auto min-h-[85vh] flex flex-col justify-between">
          {/* Top Section - Headline, Badge, Tagline */}
          <div className="flex flex-col items-center text-center pt-4">
            {/* Badge */}
            <div className="inline-flex items-center px-4 py-2 bg-cyan-500/20 backdrop-blur-sm border border-cyan-400/30 rounded-full mb-4 animate-fade-in">
              <span className="text-cyan-300 font-semibold text-sm">
                Texas's Premier Pool & Spa Experts
              </span>
            </div>

            {/* Main Heading */}
            <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold text-white mb-4 leading-tight">
              Transform Your Backyard Into a{' '}
              <span className="bg-gradient-to-r from-cyan-400 to-blue-500 bg-clip-text text-transparent">
                Luxury Oasis
              </span>
            </h1>

            {/* Tagline */}
            <p className="text-xl md:text-2xl text-gray-200 mb-2 font-medium">
              {businessInfo.tagline}
            </p>

            {/* Description */}
            <p className="text-lg text-gray-300 max-w-2xl">
              Custom pools, luxury spas, expert repairs, and reliable maintenance services.
            </p>
          </div>

          {/* Middle Section - Empty space to show logo */}
          <div className="flex-1" />

          {/* Bottom Section - CTAs and Stats */}
          <div className="flex flex-col items-center text-center pb-8">
            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
              <Button
                onClick={scrollToContact}
                className="bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700 text-white font-bold text-lg px-8 py-6 shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:scale-105"
              >
                Get Free Quote
              </Button>
              <a href={`tel:${businessInfo.phone}`}>
                <Button className="bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white font-bold text-lg px-8 py-6 shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:scale-105">
                  <Phone className="w-5 h-5 mr-2" />
                  Call Now
                </Button>
              </a>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 max-w-4xl w-full">
              {[
                { number: '20+', label: 'Years Experience' },
                { number: '2000+', label: 'Happy Clients' },
                { number: '100%', label: 'Satisfaction Rate' }
              ].map((stat, index) => (
                <div 
                  key={index} 
                  className="bg-white/10 backdrop-blur-md rounded-xl p-6 border border-white/20 hover:bg-white/15 transition-all duration-300"
                >
                  <div className="text-4xl font-bold text-cyan-400 mb-2">{stat.number}</div>
                  <div className="text-gray-200 font-medium">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
        <ChevronDown className="w-8 h-8 text-cyan-400" />
      </div>
    </section>
  );
};

export default Hero;
